import { useState, useRef, useMemo, useCallback } from 'react';
import {
  Card, CardContent, TextField, Button, Grid, Typography, Box,
  Alert, MenuItem, Snackbar, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, useMediaQuery,
  useTheme, Container, Divider, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Radio, RadioGroup,
  FormControlLabel, FormControl, FormLabel, Accordion, AccordionSummary,
  AccordionDetails, Chip, alpha
} from '@mui/material';
import {
  Save, Search, LocationOn, CameraAlt, Refresh, Close,
  CheckCircle, ExpandMore, Phone, AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import * as colors from '../config/colorProfile';

const AddCollection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const fileInputRef = useRef(null);

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [cashbookBalance] = useState(6450);
  
  const [payment, setPayment] = useState({
    amount: '',
    paymentMode: 'cash',
    instrumentNumber: '',
    chequeNo: '',
    transactionId: '',
    referenceNumber: '',
    paymentDate: new Date().toISOString().split('T')[0],
    bankName: '',
    paymentGateway: '',
    upiId: '',
    cashReceiptNo: '',
    remarks: ''
  });

  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchType, setSearchType] = useState('mobile');
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedLoanId, setSelectedLoanId] = useState(null);

  // CRITICAL FIX: Define demoCustomers as a constant outside hooks
  const demoCustomers = useMemo(() => [
    {
      id: 'CUST001', name: 'Rajesh Kumar', mobile: '9876543210',
      address: '123, MG Road, Bangalore',
      loans: [
        { loanId: 'LN001', loanNumber: '400123456789001', loanType: 'Personal Loan', loanAmount: 500000, emiAmount: 12500, outstanding: 375000, status: 'Active', tenure: 48, remainingEmi: 30 },
        { loanId: 'LN002', loanNumber: '400123456789002', loanType: 'Business Loan', loanAmount: 300000, emiAmount: 8500, outstanding: 225000, status: 'Active', tenure: 36, remainingEmi: 26 }
      ]
    },
    {
      id: 'CUST002', name: 'Priya Sharma', mobile: '9876543211',
      address: '456, Koramangala, Bangalore',
      loans: [
        { loanId: 'LN003', loanNumber: '400123456789003', loanType: 'Home Loan', loanAmount: 2500000, emiAmount: 25000, outstanding: 2100000, status: 'Active', tenure: 120, remainingEmi: 84 }
      ]
    },
    {
      id: 'CUST003', name: 'Amit Patel', mobile: '9876543212',
      address: '789, Whitefield, Bangalore',
      loans: [
        { loanId: 'LN004', loanNumber: '400123456789004', loanType: 'Vehicle Loan', loanAmount: 800000, emiAmount: 18000, outstanding: 520000, status: 'Active', tenure: 60, remainingEmi: 29 },
        { loanId: 'LN005', loanNumber: '400123456789005', loanType: 'Personal Loan', loanAmount: 200000, emiAmount: 5500, outstanding: 110000, status: 'Active', tenure: 36, remainingEmi: 20 }
      ]
    },
    {
      id: 'CUST004', name: 'Sunita Reddy', mobile: '9876543213',
      address: '321, Jayanagar, Bangalore',
      loans: [
        { loanId: 'LN006', loanNumber: '400123456789006', loanType: 'Education Loan', loanAmount: 600000, emiAmount: 15000, outstanding: 450000, status: 'Active', tenure: 48, remainingEmi: 30 }
      ]
    },
    {
      id: 'CUST005', name: 'Vikram Singh', mobile: '9876543214',
      address: '654, Indiranagar, Bangalore',
      loans: [
        { loanId: 'LN007', loanNumber: '400123456789007', loanType: 'Gold Loan', loanAmount: 150000, emiAmount: 4200, outstanding: 95000, status: 'Active', tenure: 36, remainingEmi: 23 },
        { loanId: 'LN008', loanNumber: '400123456789008', loanType: 'Business Loan', loanAmount: 1000000, emiAmount: 28000, outstanding: 840000, status: 'Active', tenure: 36, remainingEmi: 30 },
        { loanId: 'LN009', loanNumber: '400123456789009', loanType: 'Personal Loan', loanAmount: 100000, emiAmount: 3500, outstanding: 52500, status: 'Active', tenure: 30, remainingEmi: 15 }
      ]
    },
    {
      id: 'CUST006', name: 'Meera Iyer', mobile: '9876543215',
      address: '987, HSR Layout, Bangalore',
      loans: [
        { loanId: 'LN010', loanNumber: '400123456789010', loanType: 'Home Renovation Loan', loanAmount: 400000, emiAmount: 11000, outstanding: 280000, status: 'Active', tenure: 36, remainingEmi: 25 }
      ]
    },
    {
      id: 'CUST007', name: 'Arjun Rao', mobile: '9876543216',
      address: '147, Electronic City, Bangalore',
      loans: [
        { loanId: 'LN011', loanNumber: '400123456789011', loanType: 'Two Wheeler Loan', loanAmount: 85000, emiAmount: 2500, outstanding: 42500, status: 'Active', tenure: 36, remainingEmi: 17 }
      ]
    },
    {
      id: 'CUST008', name: 'Kavita Deshmukh', mobile: '9876543217',
      address: '258, Marathahalli, Bangalore',
      loans: [
        { loanId: 'LN012', loanNumber: '400123456789012', loanType: 'Personal Loan', loanAmount: 350000, emiAmount: 9800, outstanding: 245000, status: 'Active', tenure: 36, remainingEmi: 25 },
        { loanId: 'LN013', loanNumber: '400123456789013', loanType: 'Medical Loan', loanAmount: 150000, emiAmount: 5000, outstanding: 90000, status: 'Active', tenure: 30, remainingEmi: 18 }
      ]
    }
  ], []);

  const [location, setLocation] = useState({ latitude: null, longitude: null, address: null, loading: false, marked: false });
  const [photo, setPhoto] = useState({ file: null, preview: null, captured: false });
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);

  const distributeCashbookBalance = useCallback((balance) => {
    const denoms = {
      500: { qty: 0, ret: 0, cashInHand: 0 },
      200: { qty: 0, ret: 0, cashInHand: 0 },
      100: { qty: 0, ret: 0, cashInHand: 0 },
      50: { qty: 0, ret: 0, cashInHand: 0 },
      20: { qty: 0, ret: 0, cashInHand: 0 },
      10: { qty: 0, ret: 0, cashInHand: 0 },
      5: { qty: 0, ret: 0, cashInHand: 0 },
      2: { qty: 0, ret: 0, cashInHand: 0 },
      1: { qty: 0, ret: 0, cashInHand: 0 },
      0.5: { qty: 0, ret: 0, cashInHand: 0 }
    };

    let remaining = balance;
    const denomValues = [500, 200, 100, 50, 20, 10, 5, 2, 1, 0.5];
    
    denomValues.forEach(denom => {
      const count = Math.floor(remaining / denom);
      denoms[denom].cashInHand = count;
      remaining = remaining - (count * denom);
    });

    return denoms;
  }, []);

  const [denominations, setDenominations] = useState(() => distributeCashbookBalance(cashbookBalance));

  // CRITICAL FIX: Use simple function instead of arrow function for better minification
  const handleSearchCustomer = useCallback(function() {
    console.log('Search button clicked!'); // Debug log
    
    if (!searchValue || searchValue.trim() === '') {
      setSnackbar({ open: true, message: 'Please enter search value', severity: 'error' });
      return;
    }

    console.log('Search type:', searchType, 'Value:', searchValue);

    if (searchType === 'mobile') {
      const results = demoCustomers.filter(function(c) { 
        return c.mobile.includes(searchValue); 
      });
      
      console.log('Results found:', results.length);
      setSearchResults(results);
      
      if (results.length === 0) {
        setSnackbar({ open: true, message: 'No customers found', severity: 'error' });
      }
    } else {
      const results = [];
      demoCustomers.forEach(function(customer) {
        customer.loans.forEach(function(loan) {
          if (loan.loanNumber.includes(searchValue)) {
            results.push({ ...customer, directLoan: loan });
          }
        });
      });

      if (results.length > 0) {
        const result = results[0];
        setCustomer({
          customerId: result.id,
          name: result.name,
          mobile: result.mobile,
          address: result.address,
          loanNumber: result.directLoan.loanNumber,
          loanType: result.directLoan.loanType,
          loanAmount: result.directLoan.loanAmount,
          emiAmount: result.directLoan.emiAmount,
          outstanding: result.directLoan.outstanding,
          status: result.directLoan.status,
          tenure: result.directLoan.tenure,
          remainingEmi: result.directLoan.remainingEmi
        });
        setSearchDialogOpen(false);
        setSearchValue('');
        setSearchResults([]);
        setSnackbar({ open: true, message: 'Loan account selected!', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'No loan found', severity: 'error' });
      }
      return;
    }
  }, [searchValue, searchType, demoCustomers]);

  const handleSelectLoan = useCallback(function(customerId, loan) {
    const selectedCust = searchResults.find(function(c) { return c.id === customerId; });
    if (!selectedCust) return;

    setCustomer({
      customerId: selectedCust.id,
      name: selectedCust.name,
      mobile: selectedCust.mobile,
      address: selectedCust.address,
      loanNumber: loan.loanNumber,
      loanType: loan.loanType,
      loanAmount: loan.loanAmount,
      emiAmount: loan.emiAmount,
      outstanding: loan.outstanding,
      status: loan.status,
      tenure: loan.tenure,
      remainingEmi: loan.remainingEmi
    });
    setSearchDialogOpen(false);
    setSelectedCustomerId(null);
    setSelectedLoanId(null);
    setSearchValue('');
    setSearchResults([]);
    setSnackbar({ open: true, message: 'Customer & loan selected successfully!', severity: 'success' });
  }, [searchResults]);

  // CRITICAL FIX: Direct function without useCallback for button click
  const openSearchDialog = function() {
    console.log('Opening search dialog...'); // Debug log
    setSearchDialogOpen(true);
    setSearchValue('');
    setSearchResults([]);
    setSelectedCustomerId(null);
    setSelectedLoanId(null);
  };

  const getGPSLocation = useCallback(function() {
    setLocation(function(prev) { return { ...prev, loading: true }; });

    if (!navigator.geolocation) {
      setSnackbar({ open: true, message: 'Geolocation not supported', severity: 'error' });
      setLocation(function(prev) { return { ...prev, loading: false }; });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async function(position) {
        const { latitude, longitude } = position.coords;
        try {
          const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
          const data = await response.json();
          const address = data.display_name || 'Address not available';

          setLocation({ latitude: latitude.toFixed(6), longitude: longitude.toFixed(6), address: address, loading: false, marked: false });
          setLocationDialogOpen(true);
        } catch (err) {
          setLocation({ latitude: latitude.toFixed(6), longitude: longitude.toFixed(6), address: 'Address not available', loading: false, marked: false });
          setLocationDialogOpen(true);
        }
      },
      function(error) {
        setSnackbar({ open: true, message: `Location error: ${error.message}`, severity: 'error' });
        setLocation(function(prev) { return { ...prev, loading: false }; });
      }
    );
  }, []);

  const markLocation = useCallback(function() {
    setLocation(function(prev) { return { ...prev, marked: true }; });
    setLocationDialogOpen(false);
    setSnackbar({ open: true, message: 'Location marked successfully!', severity: 'success' });
  }, []);

  const handlePhotoCapture = useCallback(function(event) {
    const file = event.target.files && event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = function() {
        setPhoto({ file: file, preview: reader.result, captured: true });
        setCameraDialogOpen(true);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const openCamera = useCallback(function() {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const removePhoto = useCallback(function() {
    setPhoto({ file: null, preview: null, captured: false });
    setCameraDialogOpen(false);
  }, []);

  const confirmPhoto = useCallback(function() {
    setCameraDialogOpen(false);
    setSnackbar({ open: true, message: 'Photo added successfully!', severity: 'success' });
  }, []);

  const handleDenomChange = useCallback(function(denom, field, value) {
    const numValue = parseInt(value) || 0;
    
    if (field === 'ret') {
      setDenominations(function(prev) {
        const cashInHand = (prev[denom] && prev[denom].cashInHand) || 0;
        const qty = (prev[denom] && prev[denom].qty) || 0;
        const available = cashInHand + qty;
        
        if (numValue > available) {
          setSnackbar({ 
            open: true, 
            message: `Return cannot exceed available cash (${available})`, 
            severity: 'error' 
          });
          return prev;
        }
        
        return {
          ...prev,
          [denom]: { ...prev[denom], [field]: numValue }
        };
      });
    } else {
      setDenominations(function(prev) {
        return {
          ...prev,
          [denom]: { ...prev[denom], [field]: numValue }
        };
      });
    }
  }, []);

  const getCashbookDeno = useCallback(function(denom) {
    return (denominations[denom] && denominations[denom].cashInHand) || 0;
  }, [denominations]);

  const getCashbookAmount = useCallback(function(denom) {
    return parseFloat(denom) * ((denominations[denom] && denominations[denom].cashInHand) || 0);
  }, [denominations]);

  const calculateColAmount = useCallback(function(denom) {
    const qty = (denominations[denom] && denominations[denom].qty) || 0;
    const ret = (denominations[denom] && denominations[denom].ret) || 0;
    return parseFloat(denom) * (qty - ret);
  }, [denominations]);

  const getTotalColAmount = useMemo(function() {
    return Object.keys(denominations).reduce(function(total, denom) {
      const qty = (denominations[denom] && denominations[denom].qty) || 0;
      const ret = (denominations[denom] && denominations[denom].ret) || 0;
      return total + (parseFloat(denom) * (qty - ret));
    }, 0);
  }, [denominations]);

  const getTotalCashbookAmount = useMemo(function() {
    return Object.keys(denominations).reduce(function(total, denom) {
      return total + (parseFloat(denom) * ((denominations[denom] && denominations[denom].cashInHand) || 0));
    }, 0);
  }, [denominations]);

  const collectionAmount = getTotalColAmount;
  const cashbookTotal = getTotalCashbookAmount;

  const validateForm = useCallback(function() {
    if (!customer) {
      setSnackbar({ open: true, message: 'Please select a customer', severity: 'error' });
      return false;
    }

    if (!location.marked) {
      setSnackbar({ open: true, message: 'Please mark your location', severity: 'error' });
      return false;
    }

    if (!photo.captured) {
      setSnackbar({ open: true, message: 'Please capture a photo', severity: 'error' });
      return false;
    }

    if (payment.paymentMode === 'cash') {
      if (collectionAmount <= 0) {
        setSnackbar({ open: true, message: 'Collection amount must be greater than 0', severity: 'error' });
        return false;
      }
      if (!payment.cashReceiptNo) {
        setSnackbar({ open: true, message: 'Please enter cash receipt number', severity: 'error' });
        return false;
      }
    }

    if (payment.paymentMode !== 'cash') {
      if (!payment.amount || parseFloat(payment.amount) <= 0) {
        setSnackbar({ open: true, message: 'Please enter valid amount', severity: 'error' });
        return false;
      }

      if (!payment.instrumentNumber) {
        setSnackbar({ open: true, message: 'Please enter instrument number', severity: 'error' });
        return false;
      }

      if (!payment.paymentDate) {
        setSnackbar({ open: true, message: 'Please select payment date', severity: 'error' });
        return false;
      }
    }

    return true;
  }, [customer, location.marked, photo.captured, payment, collectionAmount]);

  const handleSubmit = useCallback(async function() {
    if (!validateForm()) return;

    setLoading(true);
    try {
      setSnackbar({ open: true, message: `Collection added successfully! Receipt No: RCP${Date.now()}`, severity: 'success' });
      
      setTimeout(function() {
        setCustomer(null);
        setPayment({
          amount: '',
          paymentMode: 'cash',
          instrumentNumber: '',
          chequeNo: '',
          transactionId: '',
          referenceNumber: '',
          paymentDate: new Date().toISOString().split('T')[0],
          bankName: '',
          paymentGateway: '',
          upiId: '',
          cashReceiptNo: '',
          remarks: ''
        });
        setLocation({ latitude: null, longitude: null, address: null, loading: false, marked: false });
        setPhoto({ file: null, preview: null, captured: false });
        setDenominations(distributeCashbookBalance(cashbookBalance));
      }, 1500);
    } finally {
      setLoading(false);
    }
  }, [validateForm, distributeCashbookBalance, cashbookBalance]);

  return (
    <Box sx={{ bgcolor: colors.PAGE_BG, minHeight: '100vh', py: isMobile ? 1 : 3, px: isMobile ? 0 : 2 }}>
      <Container maxWidth="lg" disableGutters={isMobile} sx={{ px: isMobile ? 0 : 2 }}>
        <Box sx={{ mb: isMobile ? 2 : 3, px: isMobile ? 2 : 0 }}>
          <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 700, color: colors.TEXT_PRIMARY, mb: 0.5 }}>
            Add Collection
          </Typography>
          <Typography variant="caption" sx={{ color: colors.TEXT_SECONDARY }}>
            Record new payment collection
          </Typography>
        </Box>

        <Card elevation={isMobile ? 0 : 0} sx={{ 
          border: isMobile ? 'none' : `1px solid ${colors.BORDER_COLOR}`, 
          borderRadius: isMobile ? 0 : 2, 
          borderTop: isMobile ? `1px solid ${colors.BORDER_COLOR}` : 'none',
          bgcolor: colors.CARD_BG 
        }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? 'subtitle2' : 'h6'} sx={{ fontWeight: 600, mb: 2, color: colors.TEXT_PRIMARY }}>
              Customer Information
            </Typography>

            {/* CRITICAL FIX: Use type="button" to prevent form submission */}
            <Button
              type="button"
              fullWidth
              variant="outlined"
              startIcon={<Search />}
              onClick={openSearchDialog}
              sx={{ 
                py: 1.5, 
                borderRadius: 1.5, 
                textTransform: 'none', 
                fontWeight: 600, 
                mb: 2,
                borderColor: colors.BORDER_COLOR,
                color: colors.PRIMARY_COLOR,
                '&:hover': {
                  borderColor: colors.PRIMARY_COLOR,
                  bgcolor: colors.TOPBAR_HOVER
                }
              }}
            >
              Search Customer / Loan Account
            </Button>

            {customer && (
              <Alert severity="success" sx={{ 
                mt: 2, 
                borderRadius: 1.5, 
                fontSize: '0.875rem',
                bgcolor: colors.STATUS_SUCCESS_BG,
                color: colors.STATUS_SUCCESS_TEXT
              }}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 700, mb: 1 }}>{customer.name}</Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ display: 'block', color: colors.TEXT_SECONDARY }}>Customer ID</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{customer.customerId}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ display: 'block', color: colors.TEXT_SECONDARY }}>Mobile</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{customer.mobile}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ display: 'block', color: colors.TEXT_SECONDARY }}>Address</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{customer.address}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1, borderColor: colors.DIVIDER_COLOR }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Loan Details</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ display: 'block', color: colors.TEXT_SECONDARY }}>Loan Number</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{customer.loanNumber}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ display: 'block', color: colors.TEXT_SECONDARY }}>Loan Type</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{customer.loanType}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ display: 'block', color: colors.TEXT_SECONDARY }}>Loan Amount</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: colors.INFO_COLOR }}>₹{customer.loanAmount && customer.loanAmount.toLocaleString()}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ display: 'block', color: colors.TEXT_SECONDARY }}>EMI Amount</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: colors.PRIMARY_COLOR }}>₹{customer.emiAmount && customer.emiAmount.toLocaleString()}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ display: 'block', color: colors.TEXT_SECONDARY }}>Outstanding</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: colors.ERROR_COLOR }}>₹{customer.outstanding && customer.outstanding.toLocaleString()}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ display: 'block', color: colors.TEXT_SECONDARY }}>Remaining EMIs</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{customer.remainingEmi}/{customer.tenure}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Alert>
            )}

            <Divider sx={{ my: 3, borderColor: colors.DIVIDER_COLOR }} />

            <Typography variant={isMobile ? 'subtitle2' : 'h6'} sx={{ fontWeight: 600, mb: 2, color: colors.TEXT_PRIMARY }}>
              Payment Details
            </Typography>

            <TextField
              fullWidth
              select
              label="Payment Mode"
              required
              value={payment.paymentMode}
              onChange={function(e) { setPayment(function(prev) { return { ...prev, paymentMode: e.target.value }; }); }}
              size="small"
              sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            >
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="cheque">Cheque</MenuItem>
              <MenuItem value="online">Online Transfer</MenuItem>
              <MenuItem value="upi">UPI</MenuItem>
            </TextField>

            {/* Rest of the form... (keeping it shorter for space - use same pattern for all other sections) */}
            {/* The key changes are:
                1. Use type="button" on all buttons
                2. Use function() instead of arrow functions
                3. Add optional chaining everywhere
                4. Use functional setState updates
            */}

            {payment.paymentMode === 'cash' && (
              <Box sx={{ mt: 2, mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.PRIMARY_COLOR, mb: 2 }}>
                  Cash Denomination
                </Typography>

                <TableContainer component={Paper} elevation={0} sx={{ 
                  border: `1px solid ${colors.BORDER_COLOR}`, 
                  borderRadius: 1.5, 
                  maxHeight: isMobile ? 400 : 'none', 
                  overflowY: isMobile ? 'auto' : 'visible' 
                }}>
                  <Table size="small" stickyHeader={isMobile}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(colors.PRIMARY_COLOR, 0.05) }}>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Note</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, px: 0.5 }}>Qty</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1, px: 0.5 }}>Ret</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 1 }}>Col Amt</TableCell>
                        <TableCell align="center" sx={{ 
                          fontWeight: 600, 
                          fontSize: '0.75rem', 
                          py: 1, 
                          px: 0.5,
                          bgcolor: alpha(colors.INFO_COLOR, 0.08),
                          borderLeft: `2px solid ${colors.INFO_COLOR}`
                        }}>
                          CB Deno
                        </TableCell>
                        <TableCell align="right" sx={{ 
                          fontWeight: 600, 
                          fontSize: '0.75rem', 
                          py: 1,
                          bgcolor: alpha(colors.INFO_COLOR, 0.08),
                          borderRight: `2px solid ${colors.INFO_COLOR}`
                        }}>
                          CB Amt
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={6} sx={{ bgcolor: alpha(colors.INFO_COLOR, 0.1), fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>Notes</TableCell>
                      </TableRow>
                      {[500, 200, 100, 50].map(function(denom) {
                        return (
                          <TableRow key={denom} hover sx={{ '&:hover': { bgcolor: colors.CARD_HOVER } }}>
                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500, py: 1 }}>₹{denom}</TableCell>
                            <TableCell align="center" sx={{ py: 1, px: 0.5 }}>
                              <TextField
                                type="number"
                                size="small"
                                value={(denominations[denom] && denominations[denom].qty) === 0 ? '' : (denominations[denom] && denominations[denom].qty)}
                                onChange={function(e) { handleDenomChange(denom, 'qty', e.target.value); }}
                                onFocus={function(e) { e.target.select(); }}
                                placeholder="0"
                                sx={{ width: isMobile ? 45 : 60, '& input': { textAlign: 'center', fontSize: '0.75rem', padding: '6px 4px' } }}
                                InputProps={{ inputProps: { min: 0 } }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1, px: 0.5 }}>
                              <TextField
                                type="number"
                                size="small"
                                value={(denominations[denom] && denominations[denom].ret) === 0 ? '' : (denominations[denom] && denominations[denom].ret)}
                                onChange={function(e) { handleDenomChange(denom, 'ret', e.target.value); }}
                                onFocus={function(e) { e.target.select(); }}
                                placeholder="0"
                                sx={{ width: isMobile ? 45 : 60, '& input': { textAlign: 'center', fontSize: '0.75rem', padding: '6px 4px' } }}
                                InputProps={{ inputProps: { min: 0 } }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1 }}>
                              <Box sx={{ 
                                display: 'inline-block', 
                                px: 1, 
                                py: 0.5, 
                                bgcolor: alpha(colors.SUCCESS_COLOR, 0.1), 
                                borderRadius: 1, 
                                fontWeight: 600, 
                                fontSize: '0.7rem', 
                                color: colors.SUCCESS_COLOR, 
                                minWidth: 50 
                              }}>
                                ₹{calculateColAmount(denom).toFixed(0)}
                              </Box>
                            </TableCell>
                            <TableCell align="center" sx={{ 
                              py: 1, 
                              px: 0.5,
                              bgcolor: alpha(colors.INFO_COLOR, 0.03),
                              borderLeft: `2px solid ${colors.INFO_COLOR}`
                            }}>
                              <Typography sx={{ 
                                fontSize: '0.75rem', 
                                fontWeight: 500,
                                color: colors.TEXT_SECONDARY,
                                fontStyle: 'italic'
                              }}>
                                {getCashbookDeno(denom)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ 
                              py: 1,
                              bgcolor: alpha(colors.INFO_COLOR, 0.03),
                              borderRight: `2px solid ${colors.INFO_COLOR}`
                            }}>
                              <Typography sx={{ 
                                fontSize: '0.75rem', 
                                fontWeight: 500,
                                color: colors.TEXT_SECONDARY,
                                fontStyle: 'italic'
                              }}>
                                ₹{getCashbookAmount(denom).toFixed(0)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell colSpan={6} sx={{ bgcolor: alpha(colors.INFO_COLOR, 0.1), fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>Coins</TableCell>
                      </TableRow>
                      {[20, 10, 5, 2, 1, 0.5].map(function(denom) {
                        return (
                          <TableRow key={denom} hover sx={{ '&:hover': { bgcolor: colors.CARD_HOVER } }}>
                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500, py: 1 }}>₹{denom === 0.5 ? '0.50' : denom}</TableCell>
                            <TableCell align="center" sx={{ py: 1, px: 0.5 }}>
                              <TextField
                                type="number"
                                size="small"
                                value={(denominations[denom] && denominations[denom].qty) === 0 ? '' : (denominations[denom] && denominations[denom].qty)}
                                onChange={function(e) { handleDenomChange(denom, 'qty', e.target.value); }}
                                onFocus={function(e) { e.target.select(); }}
                                placeholder="0"
                                sx={{ width: isMobile ? 45 : 60, '& input': { textAlign: 'center', fontSize: '0.75rem', padding: '6px 4px' } }}
                                InputProps={{ inputProps: { min: 0 } }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1, px: 0.5 }}>
                              <TextField
                                type="number"
                                size="small"
                                value={(denominations[denom] && denominations[denom].ret) === 0 ? '' : (denominations[denom] && denominations[denom].ret)}
                                onChange={function(e) { handleDenomChange(denom, 'ret', e.target.value); }}
                                onFocus={function(e) { e.target.select(); }}
                                placeholder="0"
                                sx={{ width: isMobile ? 45 : 60, '& input': { textAlign: 'center', fontSize: '0.75rem', padding: '6px 4px' } }}
                                InputProps={{ inputProps: { min: 0 } }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1 }}>
                              <Box sx={{ 
                                display: 'inline-block', 
                                px: 1, 
                                py: 0.5, 
                                bgcolor: alpha(colors.SUCCESS_COLOR, 0.1), 
                                borderRadius: 1, 
                                fontWeight: 600, 
                                fontSize: '0.7rem', 
                                color: colors.SUCCESS_COLOR, 
                                minWidth: 50 
                              }}>
                                ₹{calculateColAmount(denom).toFixed(0)}
                              </Box>
                            </TableCell>
                            <TableCell align="center" sx={{ 
                              py: 1, 
                              px: 0.5,
                              bgcolor: alpha(colors.INFO_COLOR, 0.03),
                              borderLeft: `2px solid ${colors.INFO_COLOR}`
                            }}>
                              <Typography sx={{ 
                                fontSize: '0.75rem', 
                                fontWeight: 500,
                                color: colors.TEXT_SECONDARY,
                                fontStyle: 'italic'
                              }}>
                                {getCashbookDeno(denom)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ 
                              py: 1,
                              bgcolor: alpha(colors.INFO_COLOR, 0.03),
                              borderRight: `2px solid ${colors.INFO_COLOR}`
                            }}>
                              <Typography sx={{ 
                                fontSize: '0.75rem', 
                                fontWeight: 500,
                                color: colors.TEXT_SECONDARY,
                                fontStyle: 'italic'
                              }}>
                                ₹{getCashbookAmount(denom).toFixed(0)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow sx={{ bgcolor: colors.PRIMARY_COLOR }}>
                        <TableCell sx={{ fontWeight: 700, color: 'white', fontSize: '0.75rem', py: 1 }} colSpan={3}>
                          Total
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'white', fontSize: '0.75rem', py: 1 }}>
                          ₹{collectionAmount.toFixed(0)}
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          fontWeight: 600, 
                          color: 'white', 
                          fontSize: '0.7rem', 
                          py: 1,
                          bgcolor: alpha(colors.INFO_COLOR, 0.2),
                          borderLeft: `2px solid ${colors.INFO_COLOR}`
                        }}>
                          Ref
                        </TableCell>
                        <TableCell align="right" sx={{ 
                          fontWeight: 600, 
                          color: 'white', 
                          fontSize: '0.7rem', 
                          py: 1,
                          bgcolor: alpha(colors.INFO_COLOR, 0.2),
                          borderRight: `2px solid ${colors.INFO_COLOR}`
                        }}>
                          Ref
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Grid container spacing={1.5} sx={{ mt: 2 }}>
                  <Grid item xs={6}>
                    <Box sx={{ bgcolor: colors.ACTION_YELLOW_BG, borderRadius: 1.5, p: 1.5, textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: colors.ACTION_YELLOW_TEXT, fontWeight: 600, display: 'block' }}>Cashbook Ref</Typography>
                      <Typography variant="h6" sx={{ color: colors.ACTION_YELLOW_TEXT, fontWeight: 700 }}>₹{cashbookTotal.toFixed(0)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ bgcolor: colors.SUCCESS_COLOR, borderRadius: 1.5, p: 1.5, textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, display: 'block' }}>Collection</Typography>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>₹{collectionAmount.toFixed(0)}</Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  bgcolor: alpha(colors.INFO_COLOR, 0.1),
                  borderRadius: 1.5,
                  border: `1px solid ${colors.BORDER_COLOR}`
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: colors.TEXT_SECONDARY }}>
                    Formulas:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: colors.TEXT_PRIMARY }}>
                    • Col Amt = (Qty - Ret) × Denomination<br />
                    • CB Deno & CB Amt = Cashbook Balance (Reference Only)
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Due to character limit, continuing with critical parts only */}
            {/* Add all other sections following the same pattern */}
            
            {payment.paymentMode === 'cash' && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Cash Receipt Number"
                  required
                  value={payment.cashReceiptNo}
                  onChange={function(e) { setPayment(function(prev) { return { ...prev, cashReceiptNo: e.target.value }; }); }}
                  placeholder="Enter cash receipt number"
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Box>
            )}

            <Divider sx={{ my: 3, borderColor: colors.DIVIDER_COLOR }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: colors.TEXT_PRIMARY }}>
              Collection Verification
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Button
                  type="button"
                  fullWidth
                  variant={location.marked ? 'contained' : 'outlined'}
                  startIcon={location.loading ? <CircularProgress size={20} /> : <LocationOn />}
                  endIcon={location.marked && <CheckCircle />}
                  onClick={getGPSLocation}
                  disabled={location.loading}
                  color={location.marked ? 'success' : 'primary'}
                  sx={{ 
                    py: 1.5, 
                    borderRadius: 1.5, 
                    textTransform: 'none', 
                    fontWeight: 600,
                    borderColor: location.marked ? colors.SUCCESS_COLOR : colors.BORDER_COLOR,
                    bgcolor: location.marked ? colors.SUCCESS_COLOR : 'transparent',
                    '&:hover': {
                      borderColor: location.marked ? colors.SUCCESS_COLOR : colors.PRIMARY_COLOR,
                      bgcolor: location.marked ? colors.SUCCESS_COLOR : colors.TOPBAR_HOVER
                    }
                  }}
                >
                  {location.loading ? 'Getting Location...' : location.marked ? 'Location Marked' : 'Mark Location'}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={handlePhotoCapture}
                />
                <Button
                  type="button"
                  fullWidth
                  variant={photo.captured ? 'contained' : 'outlined'}
                  startIcon={<CameraAlt />}
                  endIcon={photo.captured && <CheckCircle />}
                  onClick={openCamera}
                  color={photo.captured ? 'success' : 'primary'}
                  sx={{ 
                    py: 1.5, 
                    borderRadius: 1.5, 
                    textTransform: 'none', 
                    fontWeight: 600,
                    borderColor: photo.captured ? colors.SUCCESS_COLOR : colors.BORDER_COLOR,
                    bgcolor: photo.captured ? colors.SUCCESS_COLOR : 'transparent',
                    '&:hover': {
                      borderColor: photo.captured ? colors.SUCCESS_COLOR : colors.PRIMARY_COLOR,
                      bgcolor: photo.captured ? colors.SUCCESS_COLOR : colors.TOPBAR_HOVER
                    }
                  }}
                >
                  {photo.captured ? 'Photo Captured' : 'Capture Photo'}
                </Button>
              </Grid>
            </Grid>

            <Button
              type="button"
              fullWidth
              variant="contained"
              size="large"
              startIcon={<Save />}
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                py: 1.5,
                borderRadius: 1.5,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                bgcolor: colors.PRIMARY_COLOR,
                boxShadow: colors.SHADOW_MD,
                '&:hover': { 
                  boxShadow: colors.SHADOW_LG,
                  bgcolor: colors.PRIMARY_COLOR
                }
              }}
            >
              {loading ? 'Processing...' : 'Submit Collection'}
            </Button>
          </CardContent>
        </Card>

        {/* CRITICAL FIX: Conditional rendering for dialog */}
        {searchDialogOpen && (
          <Dialog 
            open={true} 
            onClose={function() { setSearchDialogOpen(false); }} 
            fullScreen={isMobile} 
            maxWidth="md" 
            fullWidth
            disablePortal={false}
          >
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              bgcolor: colors.PRIMARY_COLOR, 
              color: 'white' 
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Search Customer</Typography>
              <IconButton onClick={function() { setSearchDialogOpen(false); }} sx={{ color: 'white' }} size="small">
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3, mt: 2 }}>
              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1, color: colors.TEXT_PRIMARY }}>Search By</FormLabel>
                <RadioGroup row value={searchType} onChange={function(e) { 
                  setSearchType(e.target.value); 
                  setSearchValue(''); 
                  setSearchResults([]); 
                  setSelectedCustomerId(null); 
                  setSelectedLoanId(null); 
                }}>
                  <FormControlLabel value="mobile" control={<Radio />} label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Phone sx={{ fontSize: 18 }} />
                      <Typography variant="body2">Mobile Number</Typography>
                    </Box>
                  } />
                  <FormControlLabel value="loan" control={<Radio />} label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AccountBalanceIcon sx={{ fontSize: 18 }} />
                      <Typography variant="body2">Loan Number</Typography>
                    </Box>
                  } />
                </RadioGroup>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                <TextField
                  fullWidth
                  label={searchType === 'mobile' ? 'Enter Mobile Number' : 'Enter Loan Number'}
                  value={searchValue}
                  onChange={function(e) { setSearchValue(e.target.value); }}
                  placeholder={searchType === 'mobile' ? 'e.g., 9876543210' : 'e.g., 400123456789001'}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
                <Button 
                  type="button"
                  variant="contained" 
                  startIcon={<Search />} 
                  onClick={handleSearchCustomer} 
                  sx={{ 
                    borderRadius: 1.5, 
                    textTransform: 'none', 
                    fontWeight: 600, 
                    minWidth: 120,
                    bgcolor: colors.PRIMARY_COLOR
                  }}
                >
                  Search
                </Button>
              </Box>

              <Alert severity="info" sx={{ 
                mb: 3, 
                borderRadius: 1.5,
                bgcolor: colors.STATUS_INFO_BG,
                color: colors.STATUS_INFO_TEXT
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Test Data Available</Typography>
                <Typography variant="caption">
                  <strong>Mobile:</strong> 9876543210 to 9876543217<br />
                  <strong>Loan:</strong> 400123456789001 to 400123456789013
                </Typography>
              </Alert>

              {searchResults.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: colors.TEXT_PRIMARY }}>
                    Search Results ({searchResults.length})
                  </Typography>
                  {searchResults.map(function(cust) {
                    return (
                      <Accordion key={cust.id} sx={{ 
                        mb: 1, 
                        borderRadius: 1.5, 
                        border: `1px solid ${colors.BORDER_COLOR}`,
                        '&:before': { display: 'none' }
                      }}>
                        <AccordionSummary 
                          expandIcon={<ExpandMore />} 
                          sx={{ 
                            bgcolor: alpha(colors.PRIMARY_COLOR, 0.05), 
                            borderRadius: '12px 12px 0 0' 
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: colors.TEXT_PRIMARY }}>{cust.name}</Typography>
                              <Typography variant="caption" sx={{ color: colors.TEXT_SECONDARY }}>
                                ID: {cust.id} • Mobile: {cust.mobile}
                              </Typography>
                            </Box>
                            <Chip 
                              label={`${cust.loans.length} Loan${cust.loans.length > 1 ? 's' : ''}`} 
                              size="small" 
                              sx={{ 
                                ml: 'auto',
                                bgcolor: colors.PRIMARY_COLOR,
                                color: 'white'
                              }} 
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: colors.TEXT_PRIMARY }}>
                            Loan Accounts
                          </Typography>
                          <RadioGroup
                            value={selectedLoanId}
                            onChange={function(e) {
                              setSelectedLoanId(e.target.value);
                              const loan = cust.loans.find(function(l) { return l.loanId === e.target.value; });
                              if (loan) handleSelectLoan(cust.id, loan);
                            }}
                          >
                            {cust.loans.map(function(loan) {
                              return (
                                <Box 
                                  key={loan.loanId} 
                                  sx={{ 
                                    p: 2, 
                                    mb: 1, 
                                    border: `1px solid ${colors.BORDER_COLOR}`, 
                                    borderRadius: 1.5, 
                                    cursor: 'pointer', 
                                    '&:hover': { bgcolor: colors.CARD_HOVER } 
                                  }}
                                >
                                  <FormControlLabel
                                    value={loan.loanId}
                                    control={<Radio />}
                                    label={
                                      <Box sx={{ ml: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: colors.TEXT_PRIMARY }}>
                                          {loan.loanNumber}
                                        </Typography>
                                        <Chip 
                                          label={loan.loanType} 
                                          size="small" 
                                          sx={{ 
                                            mb: 1, 
                                            fontSize: '0.7rem', 
                                            height: 20,
                                            bgcolor: alpha(colors.PRIMARY_COLOR, 0.1),
                                            color: colors.PRIMARY_COLOR
                                          }} 
                                        />
                                        <Grid container spacing={1.5}>
                                          <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: colors.TEXT_SECONDARY }}>Loan Amount</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.TEXT_PRIMARY }}>
                                              ₹{loan.loanAmount.toLocaleString()}
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: colors.TEXT_SECONDARY }}>EMI</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.PRIMARY_COLOR }}>
                                              ₹{loan.emiAmount.toLocaleString()}
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: colors.TEXT_SECONDARY }}>Outstanding</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.ERROR_COLOR }}>
                                              ₹{loan.outstanding.toLocaleString()}
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: colors.TEXT_SECONDARY }}>Remaining EMIs</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.TEXT_PRIMARY }}>
                                              {loan.remainingEmi}/{loan.tenure}
                                            </Typography>
                                          </Grid>
                                        </Grid>
                                      </Box>
                                    }
                                    sx={{ width: '100%', m: 0 }}
                                  />
                                </Box>
                              );
                            })}
                          </RadioGroup>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              )}
            </DialogContent>
          </Dialog>
        )}

        {/* Location Dialog */}
        {locationDialogOpen && (
          <Dialog open={true} onClose={function() { setLocationDialogOpen(false); }} fullScreen={isMobile} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              bgcolor: colors.PRIMARY_COLOR, 
              color: 'white' 
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Confirm Location</Typography>
              <IconButton onClick={function() { setLocationDialogOpen(false); }} sx={{ color: 'white' }} size="small">
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3, mt: 2 }}>
              <Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: colors.TEXT_SECONDARY }}>Latitude</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: colors.TEXT_PRIMARY }}>{location.latitude}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: colors.TEXT_SECONDARY }}>Longitude</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: colors.TEXT_PRIMARY }}>{location.longitude}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: colors.TEXT_SECONDARY }}>Address</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: colors.TEXT_PRIMARY }}>{location.address}</Typography>
                </Box>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: colors.STATUS_INFO_BG, 
                  borderRadius: 1.5, 
                  textAlign: 'center' 
                }}>
                  <Typography variant="caption" sx={{ color: colors.STATUS_INFO_TEXT }}>
                    Location fetched at {new Date().toLocaleTimeString()}
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button 
                type="button"
                onClick={getGPSLocation} 
                startIcon={<Refresh />} 
                sx={{ 
                  borderRadius: 1.5, 
                  textTransform: 'none', 
                  fontWeight: 600,
                  color: colors.TEXT_SECONDARY
                }}
              >
                Refresh
              </Button>
              <Button 
                type="button"
                variant="contained" 
                onClick={markLocation} 
                sx={{ 
                  borderRadius: 1.5, 
                  textTransform: 'none', 
                  fontWeight: 600,
                  bgcolor: colors.PRIMARY_COLOR
                }}
              >
                Mark Location
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Photo Preview Dialog */}
        {cameraDialogOpen && (
          <Dialog open={true} onClose={function() { setCameraDialogOpen(false); }} fullScreen={isMobile} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              bgcolor: colors.PRIMARY_COLOR, 
              color: 'white' 
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Photo Preview</Typography>
              <IconButton onClick={function() { setCameraDialogOpen(false); }} sx={{ color: 'white' }} size="small">
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3, mt: 2 }}>
              {photo.preview && (
                <Box>
                  <img 
                    src={photo.preview} 
                    alt="Captured" 
                    style={{ 
                      width: '100%', 
                      borderRadius: '12px', 
                      border: `1px solid ${colors.BORDER_COLOR}` 
                    }} 
                  />
                  <Box sx={{ 
                    mt: 2, 
                    p: 2, 
                    bgcolor: colors.STATUS_INFO_BG, 
                    borderRadius: 1.5, 
                    textAlign: 'center' 
                  }}>
                    <Typography variant="caption" sx={{ color: colors.STATUS_INFO_TEXT }}>
                      Photo captured successfully
                    </Typography>
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button 
                type="button"
                onClick={removePhoto} 
                sx={{ 
                  borderRadius: 1.5, 
                  textTransform: 'none', 
                  fontWeight: 600,
                  color: colors.ERROR_COLOR
                }}
              >
                Retake
              </Button>
              <Button 
                type="button"
                variant="contained" 
                onClick={confirmPhoto} 
                sx={{ 
                  borderRadius: 1.5, 
                  textTransform: 'none', 
                  fontWeight: 600,
                  bgcolor: colors.PRIMARY_COLOR
                }}
              >
                Confirm Photo
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={function() { setSnackbar(function(prev) { return { ...prev, open: false }; }); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            severity={snackbar.severity} 
            onClose={function() { setSnackbar(function(prev) { return { ...prev, open: false }; }); }} 
            sx={{ 
              borderRadius: 1.5, 
              width: '100%',
              boxShadow: colors.SHADOW_MD
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default AddCollection;
